import { atom, createStore } from 'jotai'
import { useImmerAtom } from 'jotai-immer'
import type {
  ChatWorkspaceBase,
  Task,
} from 'src/services/apollo/generated/graphql'
import type { TaskStatus } from 'src/utils/task'

export enum ConversationType {
  StartPoint = 'start-point', // The initial image
  Chat = 'chat', // Generic Chat Editing
  Enhance = 'enhance', // Image Enhancement Plugin,
  Upscale = 'upscale', // Image Upscale Plugin
}

export type PresetToolGroup =
  | 'image-enhancement' // Image Enhancement Tools from Enhance Image tab in generation panel
  | 'upscale' // Upscale Tools from Enhance button on the image in generation panel

/**
 * Represents a single conversation history item.
 */
export type ConversationHistory = {
  /**
   * ID of the task in the conversation history.
   * If the id is '0', it represents the start point of the conversation.
   * If the id is a UUID, it represents a temp task that just be submitted by user
   * but not yet be created in the backend.
   * Otherwise, it will be the task id of the generation task.
   */
  id: string
  /**
   * The prompt of the task. If the task is NOT a chat task, this will be an empty string.
   */
  prompt: string
  status: TaskStatus
  type: ConversationType
  /**
   * The reference image ID for the task.
   * The start point will have no reference image.
   */
  referenceImageId?: string
  /**
   * The output image ID for the task.
   * The start point will always have this be set to the initial image.
   */
  outputImageId?: string
  creditCost?: number
  rawTask?: Task
}

export type ConversationReaction = {
  taskId: string
  mediaId: string
  likeOrDislike?: boolean
}

export type ConversationUIStatus = {
  panel: string // 'free-edit' | 'enhance-tools'
  selectedItemId: string
  isBusy: boolean
  isDisabled: boolean
  isLoading: boolean
}

export type ConversationWorkspaceStatus = {
  workspace?: Omit<ChatWorkspaceBase, '__typename'>
  history: ConversationHistory[]
  reactions: ConversationReaction[]
}

export type PresetToolSelection = {
  group: PresetToolGroup
  id: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: Record<string, any>
}

const conversationalStateStore = createStore()

export const conversationUIStatusAtom = atom<ConversationUIStatus>({
  panel: 'free-edit',
  selectedItemId: '0',
  isBusy: false,
  isDisabled: false,
  isLoading: true,
})
export const conversationWorkspaceAtom = atom<ConversationWorkspaceStatus>({
  workspace: undefined,
  history: [],
  reactions: [],
})
export const presetToolSelectionAtom = atom<PresetToolSelection>({
  group: 'image-enhancement',
  id: 'handfix',
  extra: {},
})

export const useConversationalEditUIState = () => {
  return useImmerAtom(conversationUIStatusAtom, {
    store: conversationalStateStore,
  })
}
export const useConversationalEditWorkspaceState = () => {
  return useImmerAtom(conversationWorkspaceAtom, {
    store: conversationalStateStore,
  })
}
export const usePresetToolSelectionState = () => {
  return useImmerAtom(presetToolSelectionAtom, {
    store: conversationalStateStore,
  })
}
